import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  HomeIcon,
  UsersIcon,
  DocumentTextIcon,
  MenuIcon,
  XIcon,
  LogoutIcon,
  UserGroupIcon,
  ChartBarIcon,
  TranslateIcon,
  OfficeBuildingIcon,
  ClipboardCheckIcon,
  ClipboardListIcon,
  CogIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  TruckIcon,
  CurrencyDollarIcon,
  MailIcon,
  CollectionIcon,
} from '@heroicons/react/outline';

export default function DashboardLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  // Add safety check for user and auth context
  if (!user) {
    console.warn('DashboardLayout: No user found in auth context');
  }

  // Base navigation items
  const navigation = [
    { name: 'navigation.dashboard', href: '/dashboard', icon: HomeIcon },
    { name: 'navigation.policies', href: '/policies', icon: ClipboardListIcon },
    { name: 'navigation.claims', href: '/claims', icon: ClipboardCheckIcon },
    { name: 'navigation.documents', href: '/documents', icon: DocumentTextIcon },
  ];

  // Admin-only navigation items
  const adminNavigation = [
    { name: 'navigation.users', href: '/users', icon: UserGroupIcon },
    { name: 'navigation.roles', href: '/roles', icon: ShieldCheckIcon },
    { name: 'navigation.governingBodies', href: '/governing-bodies', icon: ShieldCheckIcon },
    { name: 'navigation.companies', href: '/companies', icon: OfficeBuildingIcon },
    { name: 'navigation.carData', href: '/car-data', icon: TruckIcon },
    { name: 'navigation.reports', href: '/reports', icon: ChartBarIcon },
    { name: 'navigation.insuranceTypes', href: '/admin/insurance-types', icon: CollectionIcon },
    { name: 'navigation.commissionReports', href: '/admin/commissions', icon: CurrencyDollarIcon },
    { name: 'navigation.emailSettings', href: '/admin/email-settings', icon: MailIcon },
    { name: 'navigation.emailHistory', href: '/admin/email-history', icon: ClipboardListIcon },
    { name: 'navigation.auditLogs', href: '/audit-logs', icon: ClipboardListIcon },
    { name: 'navigation.systemMonitoring', href: '/system-monitoring', icon: CogIcon },
    { name: 'navigation.securityMonitoring', href: '/security-monitoring', icon: ShieldExclamationIcon },
  ];

  // Company navigation items - with safety check for companyId
  const companyId = user?.companyId || 'undefined';
  const companyNavigation = [
    { name: 'Company Details', href: `/companies/${companyId}`, icon: OfficeBuildingIcon },
    { name: 'navigation.branches', href: '/branches', icon: UserGroupIcon },
    { name: 'navigation.carData', href: '/car-data', icon: TruckIcon },
    { name: 'navigation.reports', href: '/reports', icon: ChartBarIcon },
  ];

  // Branch navigation item - with safety check for branchId
  const branchId = user?.branchId || 'undefined';
  const branchNavigation = [
    { name: 'Branch Details', href: `/branches/${branchId}`, icon: OfficeBuildingIcon },
    { name: 'navigation.carData', href: '/car-data', icon: TruckIcon },
    { name: 'navigation.reports', href: '/reports', icon: ChartBarIcon },
  ];

  // Governing body navigation items
  const governingBodyNavigation = [
    { name: 'navigation.companies', href: '/companies', icon: OfficeBuildingIcon },
    { name: 'navigation.branches', href: '/branches', icon: UserGroupIcon },
    { name: 'navigation.carData', href: '/car-data', icon: TruckIcon },
    { name: 'navigation.reports', href: '/reports', icon: ChartBarIcon },
  ];

  // Set navigation items based on user role with safety checks
  let navigationItems = [...navigation];
  
  const userRole = user?.role || '';
  
  if (userRole === 'admin') {
    navigationItems = [...navigationItems, ...adminNavigation];
  } else if (userRole === 'governing_body') {
    navigationItems = [...navigationItems, ...governingBodyNavigation];
  } else if (userRole === 'company') {
    navigationItems = [...navigationItems, ...companyNavigation];
  } else if (userRole === 'branch') {
    navigationItems = [...navigationItems, ...branchNavigation];
  }

  // Debug navigation items
  console.log('Navigation items:', navigationItems);
  console.log('Sidebar open:', sidebarOpen);

  // Remove debug console logs
  // console.log("DashboardLayout - User data:", user);
  // console.log("DashboardLayout - Is admin?", user?.role === 'admin');

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Function to render navigation item with safety check for icon
  const renderNavItem = (item, isMobile = false) => {
    const Icon = item.icon || HomeIcon; // Fallback to HomeIcon if undefined
    
    // Safety check for translation function
    const translatedName = typeof t === 'function' ? t(item.name) : item.name;
    
    return (
      <Link
        key={item.name}
        to={item.href}
        onClick={isMobile ? () => setSidebarOpen(false) : undefined}
        className={`${
          location.pathname === item.href
            ? 'bg-primary-100 text-primary-900'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        } group flex items-center px-2 py-2 ${isMobile ? 'text-base' : 'text-sm'} font-medium rounded-md`}
      >
        <Icon
          className={`${
            location.pathname === item.href
              ? 'text-primary-500'
              : 'text-gray-400 group-hover:text-gray-500'
          } ${isMobile ? 'mr-4' : 'mr-3'} flex-shrink-0 h-6 w-6`}
        />
        {translatedName}
      </Link>
    );
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 flex z-40 md:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-white">
              <div className="absolute top-0 right-0 -mr-12 pt-2">
                <button
                  type="button"
                  className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="sr-only">Close sidebar</span>
                  <XIcon className="h-6 w-6 text-white" aria-hidden="true" />
                </button>
              </div>
              {/* Mobile sidebar content */}
              <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                <div className="flex-shrink-0 flex items-center px-4">
                  <img
                    className="h-8 w-auto"
                    src="/libtamin.png"
                    alt="Your Logo"
                  />
                </div>
                <nav className="mt-5 px-2 space-y-1">
                  {navigationItems && navigationItems.length > 0 ? (
                    navigationItems.map(item => {
                      console.log('Rendering nav item:', item.name);
                      return renderNavItem(item, true);
                    })
                  ) : (
                    <div className="text-gray-500 text-sm px-2">No navigation items</div>
                  )}
                </nav>
              </div>
              {/* Mobile sidebar footer */}
              <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
                <div className="flex flex-col w-full space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700">{user?.username || 'Unknown User'}</p>
                    <p className="text-xs font-medium text-gray-500">{user?.role || 'No Role'}</p>
                  </div>
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={() => {
                        toggleLanguage();
                        setSidebarOpen(false);
                      }}
                      className="flex items-center text-sm text-gray-600 hover:text-gray-700"
                    >
                      <TranslateIcon className="h-5 w-5 mr-2" />
                      {language === 'en' ? 'عربي' : 'English'}
                    </button>
                    <button
                      onClick={() => {
                        handleLogout();
                        setSidebarOpen(false);
                      }}
                      className="flex items-center text-sm text-red-600 hover:text-red-700"
                    >
                      <LogoutIcon className="h-5 w-5 mr-2" />
                      {typeof t === 'function' ? t('common.logout') : 'Logout'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Transition.Child>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <div className="flex-1 flex flex-col min-h-0 border-r border-gray-200 bg-white">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex items-center flex-shrink-0 px-4">
                <img
                  className="h-20 w-auto"
                  src="/libtamin.png"
                  alt="Your Logo"
                />
              </div>
              <nav className="mt-5 flex-1 px-2 bg-white space-y-1">
                {navigationItems.map(item => renderNavItem(item))}
              </nav>
            </div>
            <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
              <div className="flex items-center justify-between w-full">
                <div>
                  <p className="text-sm font-medium text-gray-700">{user?.username || ''}</p>
                  <p className="text-xs font-medium text-gray-500">{user?.role || ''}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={toggleLanguage}
                    className="flex items-center text-sm text-gray-600 hover:text-gray-700"
                  >
                    <TranslateIcon className="h-5 w-5 mr-1" />
                    {language === 'en' ? 'عربي' : 'English'}
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex items-center text-sm text-red-600 hover:text-red-700"
                  >
                    <LogoutIcon className="h-5 w-5 mr-1" />
                    {typeof t === 'function' ? t('common.logout') : 'Logout'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <div className="md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3">
          <button
            type="button"
            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
            onClick={() => {
              console.log('Mobile menu button clicked');
              setSidebarOpen(true);
            }}
          >
            <span className="sr-only">Open sidebar</span>
            <MenuIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
